import React, { Component, Fragment } from 'react';
import styled from 'styled-components';
import { Icon, Typography } from 'rmwc';
import { grades, roundGradeValue } from './LetterScoreThree';
import { wlPath } from '../../helpers/whitelabel';

const subScoreList = [
  {
    type: 'net_worth_score',
    title: 'Current Net Worth (Savings)',
    scoreAssessment: grade => {
      if (grade > 0.9) {
        return "You've saved a lot already, and you're on track for your goals.";
      } else if (grade >= 0.75) {
        return "You have a good amount saved, but you'll want a little more to be safe!";
      } else {
        return 'Your savings are behind target, which may prevent you achieving your goals.';
      }
    },
    whyText:
      'Net worth is the most important factor in your financial health, because it measures how well prepared you are for your financial goals, right now.',
    methodologyText:
      'First, we calculate your Total Current Net Worth: we add up your savings, your primary home, and subtract the value of any debt you reported. We compare your Current Net Worth to the amount someone at your age and income ought to have, factoring your financial goals. If you meet or exceed this benchmark, you get a perfect score!',
  },
  {
    type: 'savings_rate_score',
    title: 'Cash Flow & Savings Rate',
    scoreAssessment: grade => {
      if (grade > 0.9) {
        return "Your budget looks great and you're saving plenty. Great work!";
      } else if (grade >= 0.75) {
        return "You're doing a good job, but your expenses are a little high!";
      } else {
        return "Try to save more now, or you won't have time to catch up.";
      }
    },
    improvementText: grade => {
      if (grade > 0.9) {
        return 'Just keep an eye on expenses and maintain your savings.';
      } else {
        return 'To get an A, aim to save ~10% of your paycheck every month.';
      }
    },
    whyText:
      'Savings is the second most important factor in your financial health.  Even if you haven’t saved a lot so far, you can make up for that by getting on the right track saving enough each month.',
    methodologyText:
      'We assess how much you are saving relative to a target rate of savings for your age and income level. Ideally you’ll be saving 10% (or more) of your earnings every month.',
  },
  {
    type: 'debt_score',
    title: 'Debt Management',
    scoreAssessment: grade => {
      if (grade > 0.9) {
        return 'Your debt load is totally under control. Great work!';
      } else if (grade >= 0.75) {
        return 'You have a little debt, but the amount is manageable.';
      } else {
        return 'You have quite a bit of debt relative to your income.';
      }
    },
    whyText:
      'Debt is usually related to challenges in managing your savings. Unfortunately,  once it’s there it becomes its own problem, because interest payments are a real obstacle to getting your financial health on track.',
    methodologyText:
      'For this score, we calculate how much debt you have relative to your income.  If your total debt is over 50% of your current income, you will score poorly. We exclude your primary mortgage from this, because that’s usually more than covered by your rent savings.',
  },
  {
    type: 'insurance_score',
    title: 'Protection & Security',
    scoreAssessment: grade => {
      if (grade > 0.9) {
        return "You've protected yourself against the most common risks.";
      } else if (grade >= 0.75) {
        return 'You could be vulnerable to job loss, disability, or unexpected death.';
      } else {
        return 'You could be vulnerable to job loss, disability, or unexpected death.';
      }
    },
    whyText:
      "Having the right protection in place will prevent the rest of your finances from going completely off track when the unfortunate happens:  whether that's an illness in the family, a tree falling on your house, or if you need to take time away from work.",
    methodologyText:
      'To prepare this score, we look at what coverages you have now and what holes in coverage you may want to fill. Ideally you’ll have life insurance, health insurance, and an up-to-date estate plan (as appropriate for your age and to your family situation).',
  },
];

const CircularButton = styled.div`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 45px;
  height: 45px;
  font-size: 2rem;
  font-weight: 500;

  background: ${props => (props.open ? props.color : 'white')};
  color: ${props => (props.open ? 'white' : props.color)};
`;

class ColorGrade extends Component {
  render() {
    const { grade, open } = this.props;

    const fullGrade = grade.letter.charAt(0);
    const postfix = grade.letter.charAt(1);

    return (
      <CircularButton className="ml-2" open={false} color={grade.color}>
        <span>{fullGrade}</span>
        <span style={{ position: 'relative', top: '6px', left: '1px' }}>
          <sup>{postfix}</sup>
        </span>
      </CircularButton>
    );
  }
}

class SubScore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
  }

  toggleOpen = () => {
    this.setState({ open: !this.state.open });
  };

  render() {
    const { subScoreObject, scores } = this.props;

    const numberGrade = scores[subScoreObject.type].number_score;
    const grade = grades[roundGradeValue(numberGrade * 100)];

    return (
      <div
        className="mb-2 pb-2"
        style={{ cursor: 'pointer', borderBottom: '1px solid #dadada' }}
        onClick={this.toggleOpen}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex' }}>
              <div style={{ minWidth: '70px' }} className="mt-0">
                <ColorGrade grade={grade} open={this.state.open} />
              </div>
              <div>
                <Typography tag="div" use="headline6" theme="text-primary-on-background">
                  {subScoreObject.title}
                </Typography>
                <Typography tag="div" use="body1" theme="text-secondary-on-background" className="mb-1">
                  {`${subScoreObject.scoreAssessment(numberGrade)} ${scores[subScoreObject.type].improvement_text}`}
                </Typography>
              </div>
            </div>
            <Icon style={{ padding: '0px 15px' }} theme="text-secondary-on-background">
              {this.state.open ? 'expand_less' : 'add'}
            </Icon>
          </div>
        </div>
        {this.state.open && (
          <div style={{ display: 'flex' }} className={`fadeIn animated`}>
            <div style={{ minWidth: '70px' }} />
            <div className="pl-4" style={{ paddingRight: '54px' }}>
              <Typography className="mt-2" tag="div" use="body1" theme="text-primary-on-background">
                <strong>Why is This Included?</strong>
              </Typography>
              <Typography tag="div" use="body1" theme="text-secondary-on-background">
                {subScoreObject.whyText}
              </Typography>
              <Typography className="mt-2" tag="div" use="body1" theme="text-primary-on-background">
                <strong>Methodology</strong>
              </Typography>
              <Typography className="mb-1" tag="div" use="body1" theme="text-secondary-on-background">
                {subScoreObject.methodologyText}
              </Typography>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export class Subscores extends Component {
  render() {
    const { scores } = this.props;

    return (
      <Fragment>
        <Typography tag="div" use="headline5" className="mt-5 mb-2">
          How did you get this score?
        </Typography>
        <Typography
          tag="div"
          use="body1"
          theme="text-primary-on-background"
          className="pb-3 mb-2"
          style={{ borderBottom: '1px solid #dadada' }}
        >
          Your Financial Health Score is based on <strong>four key areas</strong> of financial confidence. Click on any
          item below to learn more about how it is graded!  <a href={wlPath('/score-methodology')} target="_blank">See score methodology here.</a>
        </Typography>
        {subScoreList.map(subScoreObject => (
          <SubScore key={subScoreObject.type} subScoreObject={subScoreObject} scores={scores} />
        ))}
      </Fragment>
    );
  }
}
